import type { Permission } from "@sitescene/constant";
import type { ReactNode } from "react";

import { usePermissions } from "@/hooks/usePermissions";

interface GateProps {
	children: ReactNode;
	/**
	 * Rendered when the current user does not hold `permission`. Defaults
	 * to nothing (spec §3.1: "Feature Removal Strategy" — missing
	 * permissions remove the element from the DOM rather than disabling it).
	 */
	fallback?: ReactNode;
	permission: Permission;
}

/**
 * Conditionally renders children based on the current user's permissions.
 *
 * By default, missing permission => children are absent from the DOM.
 * Pass `fallback` only when a specific placeholder message is required.
 */
export const Gate = ({ children, fallback = null, permission }: GateProps) => {
	const { checkPermission } = usePermissions();
	const { allowed } = checkPermission(permission);
	return <>{allowed ? children : fallback}</>;
};
